import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, ReactElement, useRef } from "react";
import reactUseCookie from "react-use-cookie";
import { ModalState } from "../../../plugins/gatsby-source-serp/types";
import { Job } from "../../hooks/useJobsQuery";

interface IDataCollectionModal {
  modalState: ModalState;
  toggleModalState: (T: ModalState) => void;
  children: ReactElement;
  data?: Job[];
}

export default function Modal({
  modalState,
  toggleModalState,
  children,
  data,
}: IDataCollectionModal) {
  const cancelButtonRef = useRef(null);

  const [hasRegisteredWithEmail] = reactUseCookie("reg:email", "");
  const [hasRegisteredWithPhone] = reactUseCookie("reg:phone", "");
  let hasRegistered;
  if (modalState === ModalState.email && hasRegisteredWithEmail) {
    hasRegistered = true;
  } else if (
    modalState === ModalState["email,sms"] &&
    hasRegisteredWithEmail &&
    hasRegisteredWithPhone
  ) {
    hasRegistered = true;
  }

  // if job modal, wait until data is ready
  if (modalState === ModalState.job && (!data || (data && data.length === 0))) {
    return null;
  }

  return (
    <Transition.Root
      show={
        modalState === ModalState.job || (!hasRegistered && modalState !== null)
      }
      as={Fragment}
    >
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={() => {
          toggleModalState(null);
        }}
        title={`Modal: ${modalState}`}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        {modalState !== null && (
          // this stops an empty white rectangle appear when closing the modal, as none of the Dialog.Panel children are rendered during closing animation
          <div
            className="fixed inset-0 z-10 overflow-y-auto"
            data-cy={"modal-" + modalState}
          >
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        )}
      </Dialog>
    </Transition.Root>
  );
}
